import { useCallback, useState } from 'react'

import { Delete } from '@mui/icons-material'
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import { AxiosError } from 'axios'
import { Button, useNotify, useTranslate } from 'react-admin'

import { CompilationApiDTO } from '../../codegen/generated/Api.schemas'
import { useDeleteApiCompilationsCompilationId } from '../../codegen/generated/compilations'

type DeleteCompilationProps = {
  compilationId: CompilationApiDTO['id']
  compilationName: CompilationApiDTO['name']
  onDelete: () => void
}

export const DeleteCompilation = ({
  compilationId,
  compilationName,
  onDelete,
}: DeleteCompilationProps): JSX.Element => {
  const translate = useTranslate()
  const notify = useNotify()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const deleteCompilationMutation = useDeleteApiCompilationsCompilationId()

  const onDeleteCompilation = useCallback(async () => {
    setIsLoading(true)
    try {
      await deleteCompilationMutation.mutateAsync({
        compilationId,
      })

      notify(
        translate('pages.compilations.deleteCompilationModal.success', {
          label: compilationName,
        }),
        { type: 'success' },
      )

      onDelete()
      setIsLoading(false)
      setIsModalOpen(false)
    } catch (e: unknown) {
      const message =
        (e as AxiosError)?.response?.data.message || (e as AxiosError)?.response?.data || e

      notify(`${translate('pages.compilations.deleteCompilationModal.error')} - ${message}`, {
        type: 'error',
      })
      console.error('Error - delete compilation', e)
    }
  }, [
    deleteCompilationMutation.mutateAsync,
    compilationId,
    compilationName,
    notify,
    translate,
    onDelete,
  ])

  return (
    <>
      <Button
        size="small"
        color="error"
        onClick={() => setIsModalOpen(true)}
        style={{
          minWidth: 'fit-content',
        }}
      >
        <Delete
          color="error"
          fontSize="small"
          style={{
            marginLeft: '0.5rem',
          }}
        />
      </Button>
      <Dialog
        aria-labelledby="compilations-delete"
        open={isModalOpen}
        maxWidth="xs"
        fullWidth
        onClose={() => setIsModalOpen(false)}
      >
        <DialogTitle id="compilations-delete">
          {translate('pages.compilations.deleteCompilationModal.title')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" color="textPrimary">
            {translate('pages.compilations.deleteCompilationModal.description', {
              name: compilationName,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsModalOpen(false)
            }}
            label={translate('_generics.cancel')}
          />
          <Button
            variant="contained"
            color="error"
            onClick={onDeleteCompilation}
            label={translate('_generics.confirm')}
            disabled={isLoading}
          >
            <Delete />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
